import React, { FC, useMemo, memo } from 'react';

import EChart from '../EChart';

const BUBBLE_COLORS = ['#2E93fA', '#35be6a', '#546E7A', '#E91E63', '#FF9800', '#1979C9', '#D62A0D', '#FAA219'];

interface AxisMinMax {
    maxX: number;
    maxY: number;
    minX: number;
    minY: number;
}

interface MetricLabel {
    label?: string | React.ReactNode;
    name?: string;
}

interface BubbleDataItem {
    name: string;
    data: number[][];
}

interface TooltipItem {
    value: number | string;
}

export interface BubbleChartProps {
    /** Данные для графика */
    data: BubbleDataItem[];
    /** Функция форматирования tooltip */
    tooltip: (title: string, items: TooltipItem[]) => string;
    /** Минимальные и максимальные значения осей */
    axisMinMax: AxisMinMax;
    /** Выбранные метрики для подписей осей */
    _metricsChecked: MetricLabel[];
    /** Использовать lazy loading (true для дашбордов, false для отчётов) */
    lazyLoad?: boolean;
}

interface FormattedDataItem {
    group: string;
    x: number;
    y: number;
    z: number;
    color: string;
}

/**
 * Универсальный компонент Bubble Chart
 * Используется в отчётах и дашбордах
 */
const BubbleChart: FC<BubbleChartProps> = ({ 
    data, 
    tooltip, 
    axisMinMax, 
    _metricsChecked,
    lazyLoad = false,
}) => {
    const formatInputData = (inputData: BubbleDataItem[]): FormattedDataItem[] => {
        const _data: FormattedDataItem[] = [];

        if (inputData.length > 0) {
            inputData.forEach((item, index) => {
                if (item.data && item.data[0] && item.data[0].length >= 3) {
                    _data.push({
                        group: item.name,
                        x: item.data[0][0] ?? 0,
                        y: item.data[0][1] ?? 0,
                        z: item.data[0][2] ?? 0,
                        color: BUBBLE_COLORS[index % BUBBLE_COLORS.length],
                    });
                }
            });
        }

        return _data;
    };

    const chartOption = useMemo(() => {
        const formattedData = formatInputData(data);

        if (formattedData.length === 0) {
            return null;
        }

        // Находим максимальное значение z для нормализации размера
        const maxZ = Math.max(...formattedData.map((d) => d.z || 1));
        const minZ = Math.min(...formattedData.map((d) => d.z || 1));

        // Функция для расчёта размера пузырька
        const getSymbolSize = (z: number): number => {
            const minSize = 10;
            const maxSize = 60;
            if (maxZ === minZ) return (minSize + maxSize) / 2;
            return minSize + ((z - minZ) / (maxZ - minZ)) * (maxSize - minSize);
        };

        return {
            tooltip: {
                trigger: 'item',
                confine: true,
                // enterable: даёт навести курсор на tooltip и прокрутить скролл при длинном содержимом
                enterable: true,
                extraCssText: 'max-width:280px;max-height:260px;overflow:auto;white-space:normal;word-break:break-word;',
                formatter: (params: { data?: [number, number, string, number] }) => {
                    const { data } = params;
                    if (!data) return 'Нет данных';
                    
                    const items: TooltipItem[] = [
                        { value: data[0] },
                        { value: data[1] },
                        { value: data[2] },
                        { value: data[3] },
                    ];
                    
                    return tooltip('', items);
                },
            },
            legend: {
                show: false,
            },
            grid: {
                left: '3%',
                right: '7%',
                bottom: '7%',
                top: '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'value',
                max: axisMinMax.maxX + 50,
                splitLine: {
                    lineStyle: {
                        color: '#eee',
                    },
                },
                axisLine: {
                    lineStyle: {
                        color: '#eee',
                    },
                },
            },
            yAxis: {
                type: 'value',
                splitLine: {
                    lineStyle: {
                        color: '#eee',
                    },
                },
                axisLine: {
                    lineStyle: {
                        color: '#eee',
                    },
                },
            },
            series: formattedData.map((item) => ({
                name: item.group,
                type: 'scatter',
                symbolSize: getSymbolSize(item.z),
                itemStyle: {
                    color: item.color,
                    opacity: 0.7,
                },
                emphasis: {
                    itemStyle: {
                        opacity: 1,
                    },
                },
                data: [[item.x, item.y, item.group, item.z]],
            })),
            brush: {
                toolbox: ['rect', 'polygon', 'clear'],
                xAxisIndex: 0,
                yAxisIndex: 0,
            },
            toolbox: {
                show: false,
            },
        };
    }, [data, axisMinMax, tooltip]);

    if (!chartOption) {
        return (
            <div className="bubbleChart__wrapper">
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 400,
                    color: '#999'
                }}>
                    Нет данных для отображения
                </div>
            </div>
        );
    }

    return (
        <div className="bubbleChart__wrapper">
            <div className="bubbleChart__canvasWrapper">
                <div className="bubbleChart__yLabel" style={{ fontWeight: '600' }}>
                    {_metricsChecked ? _metricsChecked[1]?.label || '' : ''}
                </div>

                <EChart
                    option={chartOption}
                    style={{ height: 400, width: '100%' }}
                    lazyLoad={lazyLoad}
                    notMerge
                />
            </div>

            <div className="bubbleChart__xLabel" style={{ fontWeight: '600' }}>
                {_metricsChecked ? _metricsChecked[0]?.label || '' : ''}
            </div>
        </div>
    );
};

export default memo(BubbleChart);
